import { BaseSingleSelect } from '@hubertai/hubert-component-library';
import { EQuestionType } from '@hubertai/screening-question-types';
import type {
  TQuestionDisplayMode,
  TSingleSelectOption,
  TSingleSelectQuestionLocalized,
} from '@hubertai/screening-question-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuestionEditorContext } from '../context/create-question.context';
import { useAppSelector } from 'rtk/query/hooks';
import { selectQuestionLanguage, selectQuestionOptions } from 'rtk/questionEditorSelector';

const PREVIEW_OPTION_COUNT = 5;
const SAMPLE_OPTION_COUNT = 3;

// What the candidate would see in the given display mode, rendered by the same component the
// candidate app uses, from this question's own first few options.
const DisplayModePreview = ({ mode }: { mode: TQuestionDisplayMode }) => {
  const { t } = useTranslation('Questionsv2');
  const { questionId } = useQuestionEditorContext();
  const language = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const options = useAppSelector((state) => selectQuestionOptions(state, questionId)) as TSingleSelectOption[];

  const question = useMemo(() => {
    const own = options
      .filter((option) => option.enabled?.[language] !== false && option.label?.[language])
      .slice(0, PREVIEW_OPTION_COUNT)
      .map((option) => ({ id: option.id, label: option.label[language] }));
    const items = own.length
      ? own
      : Array.from({ length: SAMPLE_OPTION_COUNT }, (_, i) => ({
          id: `sample-${i}`,
          label: t('displayMode.sampleOption', { count: i + 1 }),
        }));

    return {
      id: questionId,
      type: EQuestionType.SingleSelect,
      display_mode: mode,
      options: items.map((item) => ({ ...item, enabled: true, replies: [], actions: [], hubiverse_tags: [] })),
    } as unknown as TSingleSelectQuestionLocalized<string>;
  }, [options, language, mode, questionId, t]);

  return (
    <div inert className="pointer-events-none w-[300px]">
      <BaseSingleSelect localizedQuestion={question} language={language} isPreview onSend={() => {}} />
    </div>
  );
};

export default DisplayModePreview;
